<template>
    <div class="entity-alert-configuration-field">
        <b-table :items="alertConfigurations" :fields="tableFields" small
            empty-text="No alerts have been configured yet. Alert configurations will appear here as alerts are encountered in the system."
            show-empty>
            <template #cell(enabled)="row">
                <b-checkbox switch :checked="row.item.enabled"
                    @change="updateAlertConfig(row.item.name, 'enabled', $event)" size="sm" />
            </template>

            <template #cell(color)="row">
                <b-dropdown size="sm" variant="outline-primary" block>
                    <template #button-content>
                        <span class="color-indicator"
                            :style="{ backgroundColor: getColorValue(row.item.color) }"></span>
                        {{ getColorText(row.item.color) }}
                    </template>
                    <b-dropdown-item v-for="color in colorOptions" :key="color.value"
                        @click="updateAlertConfig(row.item.name, 'color', color.value)"
                        :active="row.item.color === color.value">
                        <span class="color-indicator" :style="{ backgroundColor: getColorValue(color.value) }"></span>
                        {{ color.text }}
                    </b-dropdown-item>
                </b-dropdown>
            </template>

            <template #cell(presentationStyle)="row">
                <b-form-select :value="row.item.presentationStyle" :options="presentationStyleOptions"
                    @change="updateAlertConfig(row.item.name, 'presentationStyle', $event)" size="sm" />
            </template>

            <template #head(presentationStyle)>
                Display Style
                <HelpPopup :markdown="displayStyleHelp" icon="fa fa-question-circle" placement="left" class="ml-1" />
            </template>
        </b-table>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import {
    EntityAlertConfigurations,
    EntityAlertConfiguration,
    SUPPORTED_ALERT_COLORS,
    ALERT_PRESENTATION_STYLE_OPTIONS
} from '@/models/AlertConfiguration';
import HelpPopup from '@/components/HelpPopup.vue';

interface AlertConfigurationRow {
    name: string;
    enabled: boolean;
    color: string;
    presentationStyle: number;
}

@Component({
    name: 'EntityAlertConfigurationField',
    components: {
        HelpPopup
    }
})
export default class EntityAlertConfigurationField extends Vue {
    @Prop({ default: () => ({}) }) value!: EntityAlertConfigurations;

    get tableFields() {
        return [
            { key: 'name', label: 'Alert Name', sortable: true },
            { key: 'enabled', label: 'Enabled', class: 'text-center', thStyle: { width: '80px' } },
            { key: 'color', label: 'Color', thStyle: { width: '140px' } },
            { key: 'presentationStyle', label: 'Display Style', thStyle: { width: '180px' } }
        ];
    }

    get colorOptions() {
        return SUPPORTED_ALERT_COLORS.map(color => ({
            value: color.value,
            text: color.text
        }));
    }

    get presentationStyleOptions() {
        return ALERT_PRESENTATION_STYLE_OPTIONS;
    }

    get displayStyleHelp(): string {
        return `**Standard Banner:** Full banner alerts

**Small Banner:** Compact banners shown after standard alerts

**Icon Only:** Only shown in alert legend, no banner`;
    }

    get alertConfigurations(): AlertConfigurationRow[] {
        const configs = Object.keys(this.value || {}).map(alertName => ({
            name: alertName,
            enabled: this.value[alertName]?.enabled ?? true,
            color: this.value[alertName]?.color ?? 'blue',
            presentationStyle: this.value[alertName]?.presentationStyle ?? 0
        }));

        return configs.sort((a, b) => a.name.localeCompare(b.name));
    }

    getColorText(colorValue: string): string {
        const color = SUPPORTED_ALERT_COLORS.find(c => c.value === colorValue);
        return color?.text ?? colorValue;
    }

    getColorValue(colorValue: string): string {
        const colorMap: { [key: string]: string } = {
            'red': '#ff5050',
            'purple': 'rebeccapurple',
            'green': 'rgb(0, 179, 0)',
            'orange': 'orange',
            'turquoise': 'rgb(0, 167, 153)',
            'pink': 'rgb(255, 143, 162)',
            'blue': 'rgb(0, 140, 233)',
            'black': 'rgb(17, 17, 17)'
        };
        return colorMap[colorValue] ?? colorValue;
    }

    updateAlertConfig(alertName: string, property: string, newValue: any) {
        const updatedConfigurations = { ...this.value };

        if (!updatedConfigurations[alertName]) {
            updatedConfigurations[alertName] = new EntityAlertConfiguration();
        }

        updatedConfigurations[alertName] = {
            ...updatedConfigurations[alertName],
            [property]: newValue
        };

        this.$emit('input', updatedConfigurations);
    }
}
</script>

<style scoped>
.entity-alert-configuration-field {
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
    padding: 1rem;
}

.color-indicator {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 2px;
    margin-right: 6px;
    vertical-align: middle;
    border: 1px solid rgba(0, 0, 0, 0.1);
}

.table th {
    border-top: none;
}

.table-sm td {
    padding: 0.5rem;
}

.b-dropdown .color-indicator {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 2px;
    margin-right: 6px;
    vertical-align: middle;
    border: 1px solid rgba(0, 0, 0, 0.1);
}

.b-dropdown .btn {
    text-align: left;
}

.dropdown-item .color-indicator {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 2px;
    margin-right: 6px;
    vertical-align: middle;
    border: 1px solid rgba(0, 0, 0, 0.1);
}
</style>
